import { Banner, buildBannerMessage } from "app/client/components/Banner";
import { buildUpgradeMessage } from "app/client/components/DocumentUsage";
import { makeT } from "app/client/lib/localization";
import { localStorageBoolObs } from "app/client/lib/localStorageObs";
import { AppModel } from "app/client/models/AppModel";
import { isFreePlan } from "app/common/Features";

import { Disposable, dom, makeTestId } from "grainjs";

const t = makeT("ApiUsageBanner");

const testId = makeTestId("test-api-usage-banner-");

// Show the warning when 100 calls are left of the limit.
// For small limits use 10% of the limit, mostly for tests.
const API_CALLS_WARN_WHEN_LEFT = 100;
const API_CALLS_WARN_SHARE = 0.1;

// Warns that the site is running out of its monthly api calls, which are shared by api
// keys, oauth apps and mcp. Shown on site pages and inside documents, since a script
// that stops working is often noticed while working on a doc.
export class ApiUsageBanner extends Disposable {
  private readonly _currentOrg = this._app.currentOrg;
  private readonly _apiUsage = this._currentOrg?.apiUsage;
  private readonly _product = this._currentOrg?.billingAccount?.product;
  private readonly _currentUser = this._app.currentValidUser;

  constructor(private _app: AppModel) {
    super();
  }

  public buildDom() {
    const apiUsage = this._apiUsage;
    if (!apiUsage) { return null; }

    const left = Math.max(0, apiUsage.limit - apiUsage.used);
    if (left > apiCallsWarnAt(apiUsage.limit)) { return null; }

    return this._buildBanner(apiUsage.limit, left, apiUsage.month);
  }

  private _approachingPref(month: string) {
    // A billing manager may not be an owner, and still needs to dismiss the banner.
    if (!this._currentUser || !this._currentOrg) { return null; }
    return this.autoDispose(localStorageBoolObs(
      `u=${this._currentUser.id}:org=${this._currentOrg.id}:m=${month}:showApproachingApiCallsBanner`,
      true,
    ));
  }

  private _buildBanner(limit: number, left: number, month: string) {
    // No link on a paid plan, since only free plans set an api limit today.
    // Only build the banner when needed, since buildUpgradeMessage() reports an impression.
    const upgradeMessage = () => this._product && isFreePlan(this._product.name) ?
      [" ", buildUpgradeMessage(this._app, "apiUsageBanner", true, "long")] :
      null;

    // Nothing can be done about it until the count resets, so it cannot be dismissed.
    if (left === 0) {
      return dom.create(Banner, {
        content: buildBannerMessage(
          t("This site has used all {{limit}} of its monthly API calls.", { limit }),
          upgradeMessage(),
          testId("text"),
        ),
        style: "error",
        showCloseButton: false,
      });
    }

    // Kept per month, so that dismissing it now still warns again after the count resets.
    const pref = this._approachingPref(month);
    return dom.domComputed((use) => {
      if (pref && !use(pref)) { return null; }

      return dom.create(Banner, {
        content: buildBannerMessage(
          t("This site has {{left}} of its {{limit}} monthly API calls left.", { left, limit }),
          upgradeMessage(),
          testId("text"),
        ),
        style: "warning",
        showCloseButton: true,
        onClose: () => pref?.set(false),
      });
    });
  }
}

function apiCallsWarnAt(limit: number) {
  return Math.min(API_CALLS_WARN_WHEN_LEFT, Math.floor(limit * API_CALLS_WARN_SHARE));
}
